import { AxiosError } from "axios";
import dayjs from "dayjs";
import { LaterBooking, WindowConflict } from "shared/types";
import { DEFAULT_DATE_FORMAT, tzDate } from "./dayjs";

// The server answers a booking, waitlist join or undo that would end a
// first-class package before a class already on it with the conflict in the
// response data (WindowConflictModal offers swap / extend).
export const windowConflictOf = (error: unknown): WindowConflict | null => {
  const data = (error as AxiosError<{ data?: unknown }>)?.response?.data?.data as
    | WindowConflict
    | undefined;
  return data?.code === "package_window_conflict" ? data : null;
};

// An end-date edit refused because bookings come after the new date: the
// list the admin confirms cancelling (UpdateCustomerPackageModal).
export const laterBookingsOf = (error: unknown): LaterBooking[] | null => {
  const data = (error as AxiosError<{ data?: { bookings?: LaterBooking[] } }>)
    ?.response?.data?.data;
  return Array.isArray(data?.bookings) && data!.bookings!.length > 0
    ? data!.bookings!
    : null;
};

// A Jakarta day key (YYYY-MM-DD) in the admin's date format.
export const formatDayKey = (key?: string | null): string =>
  key ? dayjs(key, "YYYY-MM-DD").format(DEFAULT_DATE_FORMAT) : "-";

// A class instant as the admin writes it elsewhere (DD/MM/YYYY HH:mm).
export const formatClassInstant = (iso: string): string =>
  tzDate(iso).format(`${DEFAULT_DATE_FORMAT} HH:mm`);

// A void refused because the sale already has sessions taught on it: the
// count the admin acknowledges before the void goes through
// (CustomerPackageCard reopens its modal with this number).
//
// Matched on the error TOKEN, not merely on a `used_sessions` field: any other
// refusal that happened to carry a count would otherwise be swallowed by the
// reopen and never reach the admin as a message.
const USED_SESSIONS_TOKEN = "messages.error.void_package_used_confirm";

export const usedSessionsOf = (error: unknown): number | null => {
  const body = (
    error as AxiosError<{
      message?: unknown;
      error?: unknown;
      data?: { used_sessions?: unknown };
    }>
  )?.response?.data;
  const token =
    (typeof body?.message === "string" && body.message) ||
    (typeof body?.error === "string" && body.error) ||
    "";
  if (!token.includes(USED_SESSIONS_TOKEN)) return null;
  const used = body?.data?.used_sessions;
  return typeof used === "number" ? used : null;
};
